import {
  pathOr,
  equals,
  cond,
  always,
  T,
  head,
  toLower,
  find,
  propEq,
  split,
  map,
  join,
  includes
} from 'ramda';
import { authenticatedUser, currentAppSession } from 'src/utilities/storage';
import { isNotEmpty } from 'src/utilities/hasValue';
import { joinWithConnector } from 'src/utilities/dashboard';
import {
  describeBusinessExplanations,
  updateBusiness,
} from 'src/store/business/business.requests';
import { mkAutoAnswerQuestion } from 'src/features/core/ChatEngine/Configs/autoAnswerConfigurator';
import { describeBorrowers, updateBorrower } from 'src/store/borrower/borrower.requests';
import { updateDeclaration } from 'src/store/declaration/declaration.requests';
import { filterIntentForThisKeyField } from 'src/features/core/ChatEngine/utilities';
import { updateLendingTerm } from 'src/store/lendingTerms/lendingterms.requests';
import { updateProperties } from 'src/store/properties/properties.requests';
import {
  getEmployment,
  updateEmployment,
} from 'src/store/employment/employment.requests';
import {
  updateMonthlyIncome,
} from 'src/store/monthlyIncome/monthlyIncome.requests';
import {
  updateRealEstateAssets,
} from 'src/store/realEstateAssets/realEstateAssets.requests';
import {
  updateAssets,
} from 'src/store/assets/assets.requests';
import {
  updateLiability,
} from 'src/store/liabilities/liabilities.requests';
import { API_RESOURCE_ADDITIONAL_LOAN, API_RESOURCE_ASSETS, API_RESOURCE_EMPLOYMENTS, API_RESOURCE_LIABILITIES, API_RESOURCE_LOAN_APPS, API_RESOURCE_MONTHLY_INCOMES, API_RESOURCE_REAL_ESTATE_ASSET, doNothing, LABELS, OBJECT_META, USER_WORKFLOW_INTENT, YES_UPPERCASE_Y } from 'src/services/constants';
import { useSessionHook } from 'src/hooks/useSessionHook';
import { getIntentByKeyField } from '../Engine/question';
import { updateBorrowerAccounts } from 'src/store/applicantAccounts/accounts.requests';
import store from 'src/store';
import { getResourcesOfAuthenticatedUser } from 'src/store/store.helpers';
import { DO_YOU_ANSWER_ONBEHALF_CO, KEY_FIELD } from '../../Intents/intentConstants';
import { firstName, getFullName } from 'src/utilities/naming';
import { APIResourceValueEvaluator } from '../Configs/apiResourceFields';
import { ReduxStateStore } from 'src/features/core/ChatEngine/Configs/apiResouces';
import { Intent } from 'src/types/Survey';
import {
  API_RESOURCE_BORROWERS,
  API_RESOURCE_BUSINESS,
  API_RESOURCE_DECLARATIONS,
  API_RESOURCE_LENDING_TERMS,
  API_RESOURCE_LOAN_STATUS,
  API_RESOURCE_PROPERTIES,
  API_RESOURCE_USER_ACCOUNTS,
} from 'src/services/constants';
import { updateCollateral } from 'src/store/collateral/collateral.requests';

/**
 * getAdjustedWithPreBulitAnswerForThisQuestion - it will adjust the question with key field answer if key field
 * is given as "___key_field___" in a question with a flag for pre built
 * @param questionString can have a question
 * @param siteIntents can have a overall site intent
 * @param userSpecificIntent can have a over all user intent record
 */
export const getAdjustedWithPreBulitAnswerForThisQuestion = (
  questionString: string,
  siteIntents: any,
  storeState: any
) => {
  const splittedQuestion = split('___', questionString);
  if (splittedQuestion) {
    const mapSplitedQuesWithKey =
      map((splitWrd: string) => {
        return getAnswerAndMkAttachment(
          splitWrd,
          siteIntents,
          storeState
        );
      }, splittedQuestion);

    return join('', mapSplitedQuesWithKey);
  }
  return questionString;
};


/**
 * getAnswerAndMkAttachment - will get the answer if that intent has pre defined answer and
 * will give the answer from api resource for specific key
 * @param splittedKey - key from which intent answer has to be used for pre difined answer
 * @param siteIntents  - intents from intent configuration file
 * @param storeState - reducer store
 * @param isToSendKey - flgag will return the key ,if its false and doenst have answer
 * @returns
 */
export const getAnswerAndMkAttachment = (
  splittedKey: string,
  siteIntents: any,
  storeState: any,
  isToSendKey?: boolean
) => {
  const { workFlow } = useSessionHook();
  const intents = pathOr([], ['intents'], workFlow)
  return cond([
    [equals('email_in_signup'), always(authenticatedUser.accountEmail.get())],
    [equals('borrower_address'), () => mkBorrowerAddress(storeState, intents)],
    [equals('authorized_user_name'), () => mkAuthorizedUserNAme(storeState, intents)],
    [equals('borrower_name'), () => mkBorrowerName(storeState, intents)],
    [equals('borrower_first_name'), () => mkBorrowerFirstName(storeState, intents)],
    [equals('property_address'), () => mkPropertyAddress(storeState, intents)],
    [equals('former_address'), () => mkFormerAddress(storeState)],
    [equals('property_loan_type'), () => makeWelcomeText(storeState)],
    [equals('employer_name'), () => mkEmployerName(storeState, intents)],
    [equals('cashout_purpose'), () => mkCashOutPurpose(storeState, intents)],
    [equals('collateral_loan'), () => mkCollateralLoan(storeState, intents)],
    [equals('lender_name'), () => mkLenderName(storeState)],
    [equals('other_lender_name'), () => mkOtherLenderName(storeState)],
    [equals('type_of_id'), () => mkTypeOfId(storeState)],
    [equals('id_issuing_authority'), () => mkIdIssuingAuthority(storeState)],
    [
      T,
      () =>
        getAnswerFromKey(
          splittedKey,
          siteIntents,
          storeState,
          isToSendKey
        ),
    ],
  ])(splittedKey);
};

export const getAnswerAndReplace = (activeIntent: Intent, value: string, content: string) => {
  const store = new ReduxStateStore(activeIntent);
  const dynamicFields = { '___this_answer___': value }
  const apiResourceClass = new APIResourceValueEvaluator(store, dynamicFields)
  return apiResourceClass.replace(content);
}
const getAnswerFromKey = (
  key: any,
  siteIntents: any,
  storeState: any,
  isToSendKey?: boolean
) => {
  const answerFromApi = pathOr(
    '',
    ['answer'],
    mkAutoAnswerQuestion(
      head(filterIntentForThisKeyField(key, siteIntents) || []),
      storeState
    )
  );
  const answ = isNotEmpty(answerFromApi) && !isToSendKey ? answerFromApi : key;
  return answ;
};

const mkEmployerName = (storeState: any, intents: any) => {
  const { answer } = getIntentByKeyField(intents, 'employer_name');
  return pathOr(answer || 'your employer', ['employment', 'history', 'name'], storeState);
};
const mkCashOutPurpose = (storeState: any, intents: any) => {
  const { answer } = getIntentByKeyField(intents, 'cashout_purpose');
  return answer || pathOr('', ['property', 'object_meta', 'labels', 'cashout_purpose'], storeState);
};
const mkCollateralLoan = (storeState: any, intents: any) => {
  const { answer } = getIntentByKeyField(intents, 'collateral_loan');
  return answer || pathOr('', ['property', 'object_meta', 'labels', 'collateral_loan'], storeState);
};
const mkLenderName = (storeState: any) => {
  return pathOr(
    '',
    ['borrower', 'object_meta', 'labels', 'lender_name'],
    storeState
  );
};
const mkOtherLenderName = (storeState: any) => {
  return pathOr(
    '',
    ['borrower', 'object_meta', 'labels', 'other_lender_name'],
    storeState
  );
};
const mkPropertyAddress = (storeState: any, intents: any) => {
  const { answer } = getIntentByKeyField(intents, 'property_address');
  return _interpolateAddress({
    address: pathOr(answer, ['property', 'address'], storeState),
  });
};

const mkBorrowerAddress = (storeState: any, intents: any) => {
  const { answer:currentAddress } = getIntentByKeyField(intents, 'current_address');
  const { answer:currentAddressPrefill } = getIntentByKeyField(intents, 'current_address_prefill');
  const currentAnswer = currentAddress || currentAddressPrefill
  return _interpolateAddress({
    address: pathOr(currentAnswer, ['borrower', 'addresses', 'present'], storeState),
  });
};

const mkAuthorizedUserNAme = (storeState: any, intents: any) => {
  const { answer } = getIntentByKeyField(intents, 'authorized_user_name');
  return answer || pathOr('', ['borrower', OBJECT_META, LABELS, 'authorized_user_name'], storeState)

};


const mkBorrowerName = (storeState: any, intents: any) => {
  const { answer } = getIntentByKeyField(intents, 'full_name');
  return getFullName(pathOr({}, ['borrower', 'profile'], storeState)) ||
    getFullName(pathOr({}, ['accounts'], storeState)) || pathOr('', ['name'], answer);
};

const mkBorrowerFirstName = (storeState: any, intents: any) => {
  return firstName(pathOr({}, ['borrower', 'profile'], storeState)) ||
    firstName(pathOr({}, ['accounts'], storeState));
};

/** _interpolateAddress is used to interpolate and apply when the questions have __address__ added and our interpolator formats to the correct address to the show the borrower
Note: This is only used here.
**/
const _interpolateAddress = (savedProperty: any) => {
  const unit = pathOr('', ['address', 'unit_number'], savedProperty);
  const validUnit = isNotEmpty(unit) ? `Unit # ${unit}` : unit;

  const addresses = [
    pathOr('', ['address', 'street'], savedProperty),
    validUnit,
    pathOr('', ['address', 'city'], savedProperty),
    pathOr('', ['address', 'state'], savedProperty),
    pathOr('', ['address', 'zip_code'], savedProperty),
    pathOr('', ['address', 'country'], savedProperty),
  ];
  return joinWithConnector(addresses, ', ');
}

export const mkTypeOfId = (storeState: any) => {
  return pathOr('', ['business', 'metadata', 'type_of_id'], storeState);
};

export const mkIdIssuingAuthority = (storeState: any) => {
  return pathOr(
    '',
    ['business', 'metadata', 'id_issuing_authority'],
    storeState
  );
};
const makeWelcomeText = (storeState: any) => {
  const propertyLoanType = pathOr(
    '',
    ['property', 'object_meta', 'labels', 'property_loan_type'],
    storeState
  );
  return cond([
    [equals('Purchase'), always('purchase')],
    [equals('Refinance'), always('refinance')],
    [equals('Pre-qualification'), always('prequalification')],
    [T, always(toLower(propertyLoanType))],
  ])(propertyLoanType);
};

const mkFormerAddress = (storeState: any) => {
  return _interpolateAddress({
    address: pathOr({}, ['borrower', 'addresses', 'former', 0], storeState),
  });
};
/**
 * getUpdateFnForApiResource - this will return upadate functions for the givn api resource
 * @param apiResource this will which api resource the data will be updated
 */
// getUpdateDispatchFnForApiResource change to this
export const getUpdateFnForApiResource = (apiResource: any) => {
  return cond([
    [equals('business'), () => updateBusiness],
    [equals('borrower'), () => updateBorrower],
    [equals('property'), () => updateProperties],
    [equals('declaration'), () => updateDeclaration],
    [equals('lendingTerm'), () => updateLendingTerm],
    [equals('employment'), () => updateEmployment],
    [equals('monthlyIncome'), () => updateMonthlyIncome],
    [equals('realEstateAssets'), () => updateRealEstateAssets],
    [equals('collateral'), () => updateCollateral],

    [equals('accounts'), () => updateBorrowerAccounts],
    [equals('assets'), () => updateAssets],
    [equals('liabilities'), () => updateLiability],
  ])(apiResource);
};


export const getApiResourceName = (context: string) => {
  return cond([
    [() => includes(context, ['declaration', API_RESOURCE_DECLARATIONS]), always(API_RESOURCE_DECLARATIONS)],
    [() => includes(context, ['account', 'accounts']), always(API_RESOURCE_USER_ACCOUNTS)],
    [equals('borrower'), always(API_RESOURCE_BORROWERS)],
    [equals('business'), always(API_RESOURCE_BUSINESS)],
    [equals('property'), always(API_RESOURCE_PROPERTIES)],
    [equals('lendingTerm'), always(API_RESOURCE_LENDING_TERMS)],
    [equals('loanStatus'), always(API_RESOURCE_LOAN_STATUS)],
    [equals('employment'), always(API_RESOURCE_EMPLOYMENTS)],
    [() => includes(context, ['monthlyIncome', 'monthly_income']), always(API_RESOURCE_MONTHLY_INCOMES)],
    [equals('realEstateAssets'), always(API_RESOURCE_REAL_ESTATE_ASSET)],
    [equals('assets'), always(API_RESOURCE_ASSETS)],
    [equals('liabilities'), always(API_RESOURCE_LIABILITIES)],
    [equals('additionalLoan'), always(API_RESOURCE_ADDITIONAL_LOAN)],
    [equals('loanapps'), always(API_RESOURCE_LOAN_APPS)],
    [T, always(context)],
  ])(context);
}


export const getReducerFnForApiResource = (apiResource: any) => {
  return cond([
    [equals('employment'), () => getEmployment],
    [equals('borrower'), () => describeBorrowers],
    [equals('business'), () => describeBusinessExplanations],

    [T, () => doNothing],
  ])(apiResource);
};

export const getIdBasedOnResource = (apiResource: any) => {
  const { loanRoleId, loanAppId } = currentAppSession();
  return cond([
    [equals('employment'), always(loanRoleId)],
    [equals('borrower'), always(loanAppId)],
    [equals('business'), always(loanRoleId)],

    [T, always(loanRoleId)],
  ])(apiResource);
};

/**
 * updateApiResouceWithoutDataObject - basically some data is updated ddirectly some data needs tobe give as object
 * to redux,so after standardising the reducer methods we can remove it
 * @param apiResource this will which api resource the data will be updated
 */
export const updateApiResouceWithoutDataObject = (apiResource: any) => {
  return cond([
    [equals('property'), always(true)],
    [equals('lendingTerm'), always(true)],
    [equals('monthlyIncome'), always(true)],
    [equals('realEstateAssets'), always(true)],
    [equals('borrower'), always(true)],
    [equals('accounts'), always(true)],
    [equals('assets'), always(true)],
    [equals('liabilities'), always(true)],
    [T, always(false)],
  ])(apiResource);
};


/**
hasAnsweredYesToOnBehalfCoBorrower used to check is borrower has answered Yes to answer on behalf coborrower
check we have answer Yes for that Intent question answer from Applicant's workflow intents.
*/

export const hasAnsweredYesToOnBehalfCoBorrower = (siteIntents: any[]) => {
  const state = store.getState();
  const userSpecificIntents: any = getResourcesOfAuthenticatedUser(USER_WORKFLOW_INTENT, state)
  const intent = find(
    propEq(KEY_FIELD, DO_YOU_ANSWER_ONBEHALF_CO),
    pathOr([], ['intents'], head(userSpecificIntents))
  )
  return equals(pathOr('', ['answer'], intent), YES_UPPERCASE_Y)
}